// =============================================================================
// IMPORTS
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import api from '../services/api';
import { DATE_FIELD_HINTS, formatDateForDatabase } from '../utils/dateFormatter';
import { FormField, Record as RecordType, ViewConfig } from '../types/form';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================
export interface UseRecordFormArgs {
  isNew: boolean;
  existingRecord?: RecordType;
  tableName: string;
  searchParams: URLSearchParams;
  viewConfig?: ViewConfig;
  previewMode?: boolean;
}

interface OptionsResult {
  field: string;
  options: any[];
}

// =============================================================================
// MAIN HOOK
// =============================================================================
export function useRecordForm({
  isNew,
  existingRecord,
  tableName,
  searchParams,
  viewConfig,
  previewMode = false,
}: UseRecordFormArgs) {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [formData, setFormData] = useState<RecordType>({});
  const [fieldOptions, setFieldOptions] = useState<Record<string, any[]>>({});
  const [loadingOptions, setLoadingOptions] = useState(false);

  // ---------------------------------------------------------------------------
  // INITIAL POPULATION
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isNew && existingRecord) {
      setFormData({ ...existingRecord });
      return;
    }

    if (isNew) {
      const initial: RecordType = {};
      searchParams.forEach((value, key) => {
        if (key === 'returnTable' || key === 'returnId') return;

        if (key.endsWith('_id') || key === 'id') {
          const num = parseInt(value, 10);
          initial[key] = isNaN(num) ? value : num;
        } else {
          initial[key] = value;
        }
      });

      // Domain-specific defaults for thank_you_notes
      if (tableName === 'thank_you_notes') {
        if (!initial.note_text) {
          initial.note_text =
            "We wanted to take a moment to say thank you from the bottom of our hearts. Your presence at our wedding meant the world to us, and we're so grateful to have shared such a special day with you.";
        }
        if (!initial.gift) {
          initial.gift =
            "We also wanted to thank you for your kind and generous gift. Your contribution helped us pay for our honeymoon, and we made some unforgettable memories!";
        }
      }

      setFormData(initial);
    }
  }, [isNew, existingRecord, searchParams, tableName]);

  // ---------------------------------------------------------------------------
  // OPTION LOADING
  // ---------------------------------------------------------------------------
  const loadOptionsForFields = useCallback(
    async (fieldsToLoad: FormField[]) => {
      if (fieldsToLoad.length === 0) return;

      try {
        const promises = fieldsToLoad.map(async (field) => {
          if (!field.options_source) return { field: field.field_name, options: [] } as OptionsResult;

          const params: Record<string, any> = {};
          // Only apply options_filter when the select pulls data from a *different* table.
          // For self-referencing selects (options_source === current table) we skip the filter
          // to avoid limiting the dropdown to the current record.
          if (field.options_source !== tableName && field.options_filter) {
            params.options_filter =
              typeof field.options_filter === 'string'
                ? field.options_filter
                : JSON.stringify(field.options_filter);
            params.form_table_name = tableName;
            params.form_data = JSON.stringify(formData);
          }

          try {
            const response = await api.get(`/${field.options_source}`, { params });
            return { field: field.field_name, options: response.data || [] } as OptionsResult;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`[useRecordForm] Failed to load options for ${field.field_name}`, err);
            return { field: field.field_name, options: [] } as OptionsResult;
          }
        });

        const results = await Promise.all(promises);
        setFieldOptions((prev) => {
          const merged = { ...prev };
          results.forEach(({ field, options }) => {
            merged[field] = options.map((o: any) => ({ ...o, id: o.id }));
          });
          return merged;
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useRecordForm] Error loading options', err);
      }
    },
    [tableName]
  );

  // Separate function for loading dependent options that needs formData
  const loadDependentOptions = useCallback(
    async (fieldsToLoad: FormField[], currentFormData: RecordType) => {
      if (fieldsToLoad.length === 0) return;

      try {
        const promises = fieldsToLoad.map(async (field) => {
          if (!field.options_source) return { field: field.field_name, options: [] } as OptionsResult;

          const params: Record<string, any> = {};
          // Only apply options_filter when the select pulls data from a *different* table.
          // For self-referencing selects (options_source === current table) we skip the filter
          // to avoid limiting the dropdown to the current record.
          if (field.options_source !== tableName && field.options_filter) {
            params.options_filter =
              typeof field.options_filter === 'string'
                ? field.options_filter
                : JSON.stringify(field.options_filter);
            params.form_table_name = tableName;
            params.form_data = JSON.stringify(currentFormData);
          }

          try {
            const response = await api.get(`/${field.options_source}`, { params });
            return { field: field.field_name, options: response.data || [] } as OptionsResult;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`[useRecordForm] Failed to load options for ${field.field_name}`, err);
            return { field: field.field_name, options: [] } as OptionsResult;
          }
        });

        const results = await Promise.all(promises);
        setFieldOptions((prev) => {
          const merged = { ...prev };
          results.forEach(({ field, options }) => {
            merged[field] = options.map((o: any) => ({ ...o, id: o.id }));
          });
          return merged;
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useRecordForm] Error loading dependent options', err);
      }
    },
    [tableName]
  );

  // Load all select options when config changes
  useEffect(() => {
    if (previewMode || !viewConfig) return;

    const selectFields = viewConfig.fields.filter((f) => f.type === 'select' && f.options_source);
    if (selectFields.length === 0) return;

    setLoadingOptions(true);
    loadOptionsForFields(selectFields).finally(() => setLoadingOptions(false));
  }, [viewConfig, tableName, previewMode, loadOptionsForFields]);

  // ---------------------------------------------------------------------------
  // FIELD HANDLERS
  // ---------------------------------------------------------------------------
  const handleChange = useCallback(
    (field: FormField) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (field.type === 'json') {
        try {
          const parsed = value.trim() === '' ? null : JSON.parse(value);
          setFormData((prev) => ({ ...prev, [field.field_name]: parsed }));
        } catch {
          setFormData((prev) => ({ ...prev, [field.field_name]: value }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [field.field_name]: value }));
      }
    },
    []
  );

  const handleSelectChange = useCallback(
    (field: FormField) => (event: SelectChangeEvent) => {
      const value = event.target.value;
      setFormData((prev) => ({ ...prev, [field.field_name]: value }));

      if (!viewConfig) return;
      const dependents = viewConfig.fields.filter(
        (f) =>
          f.type === 'select' &&
          f.options_filter &&
          typeof f.options_filter === 'string' &&
          f.options_filter.includes(`this.${field.field_name}`)
      );

      if (dependents.length > 0) {
        // Use a timeout to batch updates and avoid excessive API calls
        setTimeout(() => {
          setFormData((currentFormData) => {
            loadDependentOptions(dependents, currentFormData);
            return currentFormData;
          });
        }, 100);
      }
    },
    [viewConfig, loadDependentOptions]
  );

  const handleCheckboxChange = useCallback(
    (field: FormField) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field.field_name]: event.target.checked }));
    },
    []
  );

  const handleDateChange = useCallback(
    (field: FormField) => (date: Date | null) => {
      const formatted = date ? formatDateForDatabase(date) : null;
      setFormData((prev) => ({ ...prev, [field.field_name]: formatted }));
    },
    []
  );

  // ---------------------------------------------------------------------------
  // EXPORTS
  // ---------------------------------------------------------------------------
  return {
    formData,
    setFormData,
    fieldOptions,
    loadingOptions,
    handleChange,
    handleSelectChange,
    handleCheckboxChange,
    handleDateChange,
  } as const;
} 